import { useEffect, useRef } from 'react';
import { BASE } from '../config/api';
import { agendaSelectiveRefresh } from '../api/bootstrap';
import sseTokenService from '../services/sseTokenService';

const K_LOGS_CURSOR = 'agenda/logs/cursor';

/**
 * Hook pour initialiser SSE pour l'agenda
 * Utilisé dans l'app principale après le login
 * 🔥 FIX: Utilisation de ref pour éviter les fuites de connexions SSE
 */
export function useAgendaSSE() {
  const esRef = useRef(null);
  const isMountedRef = useRef(true);
  const reconnectTimeoutRef = useRef(null);
  
  useEffect(() => {
    isMountedRef.current = true;
    
    const closeEventSource = (es) => {
      if (es) {
        try {
          es.onmessage = null;
          es.onerror = null;
          es.onopen = null;
          es.close();
        } catch (err) {
          console.warn('⚠️ [AgendaSSE] Erreur lors de la fermeture EventSource:', err);
        }
      }
    };
    
    const initAgendaSSE = async () => {
      // 🔥 FIX: Fermer l'ancienne connexion avant d'en créer une nouvelle
      if (esRef.current) {
        console.log('🔄 [AgendaSSE] Fermeture ancienne connexion avant réinitialisation');
        closeEventSource(esRef.current);
        esRef.current = null;
      }
      
      // Vérifier que le composant est encore monté
      if (!isMountedRef.current) {
        console.log('⚠️ [AgendaSSE] Composant démonté, arrêt initialisation');
        return;
      }
      
      try {
        // Obtenir un token SSE temporaire et utiliser la bonne route
        const sseToken = await sseTokenService.getCurrentToken();
        const sseUrl = `${BASE}/api/sse/dashboard?jwt=${encodeURIComponent(sseToken)}`;
        
        // Vérifier à nouveau après l'async
        if (!isMountedRef.current) {
          console.log('⚠️ [AgendaSSE] Composant démonté pendant récupération token');
          return;
        }
        
        console.log('🔗 [AgendaSSE] Initialisation SSE avec token temporaire');
        console.log('🔍 [AgendaSSE] URL SSE:', sseUrl);
        
        const es = new EventSource(sseUrl);
        esRef.current = es;
        
        es.onmessage = (e) => {
          // Vérifier que le composant est encore monté avant de traiter
          if (!isMountedRef.current) {
            console.log('⚠️ [AgendaSSE] Composant démonté, ignore message');
            return;
          }
          
          console.log('📨 [AgendaSSE] Message SSE reçu:', e.data);
          try {
            const data = JSON.parse(e.data);
            console.log('📨 [AgendaSSE] Données parsées:', data);
            agendaSelectiveRefresh(data);
          } catch (parseError) {
            console.error('❌ [AgendaSSE] Erreur parsing message:', parseError, 'Raw data:', e.data);
          }
        };
        
        es.onerror = (e) => {
          console.error('❌ [AgendaSSE] Erreur EventSource:', e);
          
          // Ne rien faire si le composant est démonté
          if (!isMountedRef.current) {
            return;
          }
          
          // Si token expiré, essayer de le renouveler
          if (!sseTokenService.isValid()) {
            console.log('🔄 [AgendaSSE] Token expiré, renouvellement...');
            
            // Annuler tout timeout de reconnexion en cours
            if (reconnectTimeoutRef.current) {
              clearTimeout(reconnectTimeoutRef.current);
              reconnectTimeoutRef.current = null;
            }
            
            sseTokenService.forceRenew().then(() => {
              // Vérifier que le composant est encore monté avant de reconnecter
              if (isMountedRef.current) {
                // Attendre un peu avant de reconnecter pour éviter les boucles
                reconnectTimeoutRef.current = setTimeout(() => {
                  if (isMountedRef.current) {
                    initAgendaSSE();
                  }
                }, 1000);
              }
            }).catch((err) => {
              console.error('❌ [AgendaSSE] Erreur renouvellement token:', err);
            });
          }
        };
        
        es.onopen = () => {
          if (isMountedRef.current) {
            console.log('✅ [AgendaSSE] Connexion SSE établie');
          }
        };
        
      } catch (error) {
        console.error('❌ [AgendaSSE] Erreur initialisation SSE:', error);
        esRef.current = null;
      }
    };
    
    // Initialiser la connexion SSE
    initAgendaSSE();

    return () => {
      isMountedRef.current = false;
      
      // Annuler tout timeout de reconnexion
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      // Fermer la connexion SSE
      if (esRef.current) {
        console.log('🧹 [AgendaSSE] Fermeture connexion SSE');
        closeEventSource(esRef.current);
        esRef.current = null;
      }
    };
  }, []);
}
